import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { useTranslation, Trans } from 'react-i18next';
import {
    Copy,
    Plus,
    Trash2,
    Zap,
    Layout,
    Check,
    ChevronsUpDown,
    CheckCircle2,
    Lock as LockIcon
} from 'lucide-react';
import axios from 'src/utils/axios';
import useAuth from 'src/hooks/useAuth';
import { verifyFeatures } from 'src/utils/functions';

import { cn } from 'src/@/lib/utils';
import { Button } from 'src/@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription
} from 'src/@/components/ui/dialog';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Switch } from 'src/@/components/ui/switch';
import { Badge } from 'src/@/components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from 'src/@/components/ui/table';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from 'src/@/components/ui/tooltip';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from 'src/@/components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from 'src/@/components/ui/popover';

export default function TriggersComponent() {
    const { enqueueSnackbar } = useSnackbar();
    const { user, cliente } = useAuth();
    const { t } = useTranslation('settings');

    const hasAccess = verifyFeatures(cliente?.features, 'automacoes.triggers');

    const PLATFORMS = [
        {
            category: t('triggers.platformCategories.financas', 'FINANÇAS'),
            options: [
                'ASAAS',
                'Pagar.me',
                'Pagbank',
                'Stripe'
            ]
        },
        {
            category: t('triggers.platformCategories.ecommerce', 'ECOMMERCE'),
            options: [
                'WBUY',
                'Kiwify',
                'LojaIntegrada',
                'NuvemShop',
                'Shopify',
                'Tray',
                'Vtex',
                'Wix',
                'Yampi',
                'Bagy',
                'Mercado Livre'
            ]
        }
    ];

    const [triggers, setTriggers] = useState([]);
    const [open, setOpen] = useState(false);
    const [newTriggerName, setNewTriggerName] = useState('');
    const [selectedPlatform, setSelectedPlatform] = useState('');
    const [openCombobox, setOpenCombobox] = useState(false);
    const [loading, setLoading] = useState(false);

    // State for the 2-step process
    const [step, setStep] = useState(0); // 0: Input, 1: Success/Result
    const [createdTrigger, setCreatedTrigger] = useState(null);

    const fetchTriggers = async () => {
        try {
            const response = await axios.get('/api/v2/automacoes/triggers');
            if (!response.data.error) {
                const formattedTriggers = response.data.data.map(item => ({
                    id: item._id,
                    name: item.trigger_name,
                    platform: item.tool_type,
                    url: `https://api.zindi.app/webhook/v1/triggers/trg_${item.trigger_hash}`,
                    active: true, // Backend doesn't support status yet, default to true
                    createdAt: item.create_at ? new Date(item.create_at).toLocaleDateString() : '-',
                    events: 0, // Backend doesn't track events count yet
                    hash: item.trigger_hash
                }));
                setTriggers(formattedTriggers);
            }
        } catch (error) {
            console.error(error);
            enqueueSnackbar(t('triggers.messages.fetchError', 'Erro ao carregar triggers'), { variant: 'error' });
        }
    };

    useEffect(() => {
        fetchTriggers();
    }, []);

    const handleOpen = () => {
        setOpen(true);
        setStep(0);
        setNewTriggerName('');
        setSelectedPlatform('');
        setCreatedTrigger(null);
    };

    const handleClose = () => {
        setOpen(false);
        // Reset after a short delay to avoid flickering while closing
        setTimeout(() => {
            setStep(0);
            setCreatedTrigger(null);
        }, 300);
    };

    const handleCreate = async () => {
        if (!newTriggerName || !selectedPlatform) return;

        setLoading(true);
        const mockId = Math.random().toString(36).substring(7);

        try {
            const payload = {
                trigger_hash: mockId,
                trigger_name: newTriggerName,
                tool_type: selectedPlatform,
                create_by: user?._id || user?.id,
                config: {}
            };

            const response = await axios.post('/api/v2/automacoes/triggers/criar', payload);

            if (!response.data.error) {
                const generatedUrl = `https://api.zindi.app/webhook/v1/triggers/trg_${mockId}`;
                const today = new Date().toLocaleDateString();

                const newTrigger = {
                    id: response.data.data._id,
                    name: newTriggerName,
                    platform: selectedPlatform,
                    url: generatedUrl,
                    active: true,
                    createdAt: today,
                    events: 0
                };

                setTriggers([newTrigger, ...triggers]);
                setCreatedTrigger(newTrigger);
                setStep(1);
                enqueueSnackbar(t('triggers.messages.createSuccess', 'Trigger criada com sucesso!'), { variant: 'success' });
            } else {
                enqueueSnackbar(response.data.mensagem || t('triggers.messages.createError', 'Erro ao criar trigger'), { variant: 'error' });
            }
        } catch (error) {
            console.error(error);
            enqueueSnackbar(t('triggers.messages.createError', 'Erro ao criar trigger'), { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        enqueueSnackbar(t('triggers.messages.copySuccess', 'URL copiada!'), { variant: 'success' });
    };

    const handleDelete = async (id) => {
        try {
            await axios.put(`/api/v2/automacoes/triggers/${id}/deletar`);
            setTriggers(triggers.filter((trigger) => trigger.id !== id));
            enqueueSnackbar(t('triggers.messages.deleteSuccess', 'Trigger removida.'), { variant: 'info' });
        } catch (error) {
            console.error(error);
            enqueueSnackbar(t('triggers.messages.deleteError', 'Erro ao remover trigger'), { variant: 'error' });
        }
    };

    // Toggle status is not yet implemented in backend, keeping UI interaction but not persisting
    const toggleStatus = (id) => {
        setTriggers(triggers.map(trigger => trigger.id === id ? { ...trigger, active: !trigger.active } : trigger));
    };

    if (!hasAccess) {
        return (
            <div className="flex flex-col items-center justify-center h-[calc(100vh-100px)] w-full p-6 animate-in fade-in zoom-in-95 duration-500">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-100 p-8 text-center relative overflow-hidden">
                    {/* Background decorations */}
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-violet-500 to-fuchsia-500" />
                    <div className="absolute -top-10 -right-10 w-32 h-32 bg-violet-50 rounded-full blur-3xl opacity-60" />
                    <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-fuchsia-50 rounded-full blur-3xl opacity-60" />

                    {/* Icon */}
                    <div className="relative mb-6 mx-auto w-20 h-20 bg-gradient-to-br from-violet-50 to-fuchsia-50 rounded-3xl flex items-center justify-center shadow-sm ring-1 ring-black/5">
                        <LockIcon className="w-10 h-10 text-violet-600" />
                    </div>

                    {/* Content */}
                    <h2 className="relative text-2xl font-bold text-slate-900 mb-3 tracking-tight">
                        {t('triggers.accessDenied.title', 'Funcionalidade Bloqueada')}
                    </h2>

                    <p className="relative text-slate-600 mb-8 leading-relaxed text-sm">
                        <Trans
                            t={t}
                            i18nKey="triggers.accessDenied.description"
                            defaults="O módulo de <strong1>Triggers e Webhooks</strong1> permite integrar seu sistema com centenas de aplicações externas. Atualmente, este recurso não está habilitado para sua conta."
                            components={{ strong1: <strong /> }}
                        />
                    </p>

                    <Button
                        className="relative w-full bg-[#7C3AED] hover:bg-[#6d28d9] text-white shadow-lg shadow-violet-500/20 h-11 text-base font-medium"
                        onClick={() => window.open('https://api.whatsapp.com/send?phone=5511999999999', '_blank')}
                    >
                        {t('triggers.accessDenied.ctaButton', 'Solicitar Acesso ao Suporte')}
                    </Button>

                    <p className="relative mt-6 text-xs text-slate-400">
                        {t('triggers.accessDenied.footerNote', 'Fale com nosso time para conhecer os planos com esta funcionalidade.')}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full w-full gap-6 p-2 md:p-0 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{t('triggers.header.title', 'Triggers')}</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        {t('triggers.header.subtitle', 'Configure webhooks de entrada para receber eventos de outras aplicações.')}
                    </p>
                </div>
                <Button
                    onClick={handleOpen}
                    className="bg-[#7C3AED] hover:bg-[#6d28d9] shadow-sm shadow-[#7C3AED]/20"
                >
                    <Plus className="mr-2 h-4 w-4" />
                    {t('triggers.actions.newTrigger', 'Nova Trigger')}
                </Button>
            </div>

            {/* List */}
            <div className="flex flex-col gap-4">
                {triggers.length === 0 ? (
                    <div className="flex flex-col items-center justify-center p-12 bg-white rounded-xl border border-gray-100 border-dashed text-center">
                        <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4 text-gray-400">
                            <Zap size={32} />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('triggers.empty.title', 'Nenhuma trigger criada')}</h3>
                        <p className="text-gray-500 max-w-sm mb-6">
                            {t('triggers.empty.description', 'Crie sua primeira trigger para começar a receber eventos externos e iniciar automações.')}
                        </p>
                        <Button variant="link" onClick={handleOpen} className="text-[#7C3AED]">
                            {t('triggers.empty.cta', 'Criar minha primeira trigger')}
                        </Button>
                    </div>
                ) : (
                    <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[300px]">{t('triggers.table.detailsHeader', 'Detalhes da Trigger')}</TableHead>
                                    <TableHead className="hidden md:table-cell">{t('triggers.fields.platform', 'Plataforma')}</TableHead>
                                    <TableHead className="hidden lg:table-cell">{t('triggers.table.webhookUrlHeader', 'Webhook URL')}</TableHead>
                                    <TableHead className="text-center w-[100px]">{t('labels.status', { ns: 'common' })}</TableHead>
                                    <TableHead className="text-right w-[100px]">{t('buttons.actions', { ns: 'common' })}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {triggers.map((trigger) => (
                                    <TableRow key={trigger.id}>
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-purple-50 rounded-lg text-purple-600">
                                                    <Zap size={18} />
                                                </div>
                                                <div>
                                                    <h3 className="text-sm font-semibold text-gray-900">{trigger.name}</h3>
                                                    <div className="flex items-center gap-2 mt-0.5">
                                                        <span className="text-[10px] text-gray-400">{t('triggers.table.createdAt', 'Criada em {{date}}', { date: trigger.createdAt })}</span>
                                                        <span className="text-[10px] text-gray-300">•</span>
                                                        <span className="text-[10px] text-gray-500">{t('triggers.table.eventsCount', '{{qty}} eventos', { qty: trigger.events.toLocaleString() })}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="hidden md:table-cell">
                                            <Badge variant="outline" className="font-normal bg-gray-50">
                                                {trigger.platform}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="hidden lg:table-cell">
                                            <div className="flex items-center gap-2 max-w-[300px]">
                                                <code className="bg-gray-100 px-2 py-1 rounded text-xs text-gray-600 truncate flex-1 font-mono">
                                                    {trigger.url}
                                                </code>
                                                <TooltipProvider>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-6 w-6"
                                                                onClick={() => handleCopy(trigger.url)}
                                                            >
                                                                <Copy size={13} />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p>{t('triggers.table.copyUrlTooltip', 'Copiar URL')}</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <Switch
                                                checked={trigger.active}
                                                onCheckedChange={() => toggleStatus(trigger.id)}
                                                className="data-[state=checked]:bg-[#7C3AED]"
                                            />
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-gray-400 hover:text-red-500 hover:bg-red-50"
                                                            onClick={() => handleDelete(trigger.id)}
                                                        >
                                                            <Trash2 size={16} />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <p>{t('triggers.table.deleteTooltip', 'Excluir Trigger')}</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            {step === 0 ? t('triggers.actions.newTrigger', 'Nova Trigger') : t('triggers.dialog.successTitle', 'Trigger Criada com Sucesso!')}
                        </DialogTitle>
                        <DialogDescription>
                            {step === 0
                                ? t('triggers.dialog.createDescription', 'Preencha os dados da nova trigger para gerar o webhook.')
                                : t('triggers.dialog.successDescription', 'Sua trigger foi gerada. Copie a URL abaixo para configurar na plataforma externa.')}
                        </DialogDescription>
                    </DialogHeader>

                    {step === 0 ? (
                        <div className="flex flex-col gap-4 py-4">
                            <div className="grid gap-2">
                                <Label htmlFor="name">{t('triggers.dialog.form.nameLabel', 'Identificação')}</Label>
                                <div className="relative">
                                    <Zap className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                    <Input
                                        id="name"
                                        placeholder={t('triggers.dialog.form.namePlaceholder', 'Ex: Pedidos Shopify')}
                                        value={newTriggerName}
                                        onChange={(e) => setNewTriggerName(e.target.value)}
                                        className="pl-9"
                                    />
                                </div>
                            </div>

                            <div className="grid gap-2">
                                <Label>{t('triggers.fields.platform', 'Plataforma')}</Label>
                                <Popover open={openCombobox} onOpenChange={setOpenCombobox}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            role="combobox"
                                            aria-expanded={openCombobox}
                                            className="w-full justify-between font-normal text-muted-foreground"
                                        >
                                            <div className="flex items-center gap-2">
                                                <Layout className="h-4 w-4 opacity-50" />
                                                {selectedPlatform
                                                    ? selectedPlatform
                                                    : t('triggers.dialog.form.platformPlaceholder', 'Selecione a plataforma...')}
                                            </div>
                                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[400px] p-0" align="start">
                                        <Command>
                                            <CommandInput placeholder={t('triggers.dialog.form.platformSearchPlaceholder', 'Buscar plataforma...')} />
                                            <CommandList>
                                                <CommandEmpty>{t('triggers.dialog.form.platformEmpty', 'Nenhuma plataforma encontrada.')}</CommandEmpty>
                                                {PLATFORMS.map((group) => (
                                                    <CommandGroup key={group.category} heading={group.category}>
                                                        {group.options.map((platform) => (
                                                            <CommandItem
                                                                key={platform}
                                                                value={platform}
                                                                onSelect={(currentValue) => {
                                                                    setSelectedPlatform(currentValue === selectedPlatform ? "" : currentValue);
                                                                    setOpenCombobox(false);
                                                                }}
                                                            >
                                                                <Check
                                                                    className={cn(
                                                                        "mr-2 h-4 w-4",
                                                                        selectedPlatform === platform ? "opacity-100" : "opacity-0"
                                                                    )}
                                                                />
                                                                {platform}
                                                            </CommandItem>
                                                        ))}
                                                    </CommandGroup>
                                                ))}
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center gap-6 py-6 animate-in zoom-in-95 duration-300">
                            <div className="flex bg-green-50 p-4 rounded-full">
                                <CheckCircle2 className="h-12 w-12 text-green-600" />
                            </div>

                            <div className="w-full space-y-2">
                                <Label className="text-center block text-muted-foreground mb-2">{t('triggers.dialog.success.urlLabel', 'Seu Webhook URL')}</Label>
                                <div className="relative">
                                    <div className="w-full p-4 pr-12 rounded-lg border bg-gray-50 text-sm font-mono break-all text-gray-700">
                                        {createdTrigger?.url}
                                    </div>
                                    <div className="absolute top-2 right-2">
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 hover:bg-white hover:text-[#7C3AED] hover:shadow-sm"
                                                        onClick={() => handleCopy(createdTrigger?.url)}
                                                    >
                                                        <Copy size={16} />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p>{t('buttons.copy', { ns: 'common' })}</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                </div>
                                <p className="text-[11px] text-center text-gray-400 px-4">
                                    <Trans
                                        t={t}
                                        i18nKey="triggers.dialog.success.urlHint"
                                        values={{ platform: createdTrigger?.platform }}
                                        components={{ bold: <span className="font-semibold text-gray-600" /> }}
                                        defaults="Utilize esta URL na configuração de webhook da plataforma <bold>{{platform}}</bold>."
                                    />
                                </p>
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        {step === 0 ? (
                            <>
                                <Button variant="outline" onClick={handleClose}>
                                    {t('buttons.cancel', { ns: 'common' })}
                                </Button>
                                <Button
                                    onClick={handleCreate}
                                    disabled={!newTriggerName || !selectedPlatform || loading}
                                    className="bg-[#7C3AED] hover:bg-[#6d28d9]"
                                >
                                    {loading ? t('triggers.dialog.form.creating', 'Criando...') : t('triggers.dialog.form.submit', 'Gerar Webhook')}
                                </Button>
                            </>
                        ) : (
                            <Button
                                onClick={handleClose}
                                className="w-full bg-gray-900 hover:bg-gray-800"
                            >
                                {t('triggers.dialog.success.doneButton', 'Concluir')}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
